import * as Yup from 'yup';
import { FormattedMessage } from 'react-intl';
import { Formik, FormikProps } from 'formik';
import {
    PoolButton,
    PoolFormikNumberTextfield,
    PoolModal,
    PoolModalActions,
    PoolModalContent,
    PoolModalTitle,
    PoolTypography,
} from '@synergy/components-next';
import { PoolFormattedMessage } from '@synergy/components';
import { ReactElement } from 'react';
import { Stack } from '@mui/material';

interface FormValues {
    payout: number | null;
}

interface BonusSettlementPayoutModalProps {
    open: boolean;
    /** The quota the settlement carries, as a percentage. */
    payout?: number | null;
    onClose: () => void;
    onSave: (payout: number) => void;
}

const validationSchema = Yup.object().shape({
    // 100 per cent is the contract: the member's bonus base already carries
    // the 70/30 split, so anything below multiplies the two (#2211).
    payout: Yup.number()
        .min(100, 'app.error.payout.min')
        .required('app.error.required')
        .typeError('app.error.required'),
});

/**
 * The settlement's payout quota.
 *
 * Lives on its own because it is offered in two places: the three-dot menu of
 * the overview and the settlement itself, where whoever works on it is
 * standing (#2211).
 */
export const BonusSettlementPayoutModal = ({
    open,
    payout,
    onClose,
    onSave,
}: BonusSettlementPayoutModalProps): ReactElement => {
    const handleClose = (formikProps: FormikProps<FormValues>) => {
        formikProps.resetForm();
        onClose();
    };

    return (
        <Formik<FormValues>
            initialValues={{ payout: payout ?? null }}
            onSubmit={values => {
                if (values.payout) {
                    onSave(values.payout);
                }
            }}
            validationSchema={validationSchema}
            enableReinitialize={true}
        >
            {formikProps => (
                <PoolModal open={open} onClose={() => handleClose(formikProps)}>
                    <PoolModalTitle>
                        <FormattedMessage id="app.bonusSettlements.modal.edit" />
                    </PoolModalTitle>
                    <PoolModalContent>
                        <Stack gap={2}>
                            <PoolFormikNumberTextfield
                                name="payout"
                                label={<FormattedMessage id="app.bonusSettlements.payout" />}
                                required={true}
                                suffix="%"
                                decimalScale={2}
                                minDecimalScale={2}
                                autoFocus={true}
                                fullWidth={true}
                            />
                            <Stack typography="body2" color="grey.800" sx={{ textAlign: 'left' }}>
                                <PoolFormattedMessage id="app.bonusSettlements.payoutRatio.helperText1" />
                                <PoolTypography
                                    typography="caption"
                                    translationId="app.bonusSettlements.payoutRatio.helperText2"
                                />
                            </Stack>
                        </Stack>
                    </PoolModalContent>
                    <PoolModalActions>
                        <PoolButton variant="outlined" onClick={() => handleClose(formikProps)}>
                            <FormattedMessage id="app.common.cancel" />
                        </PoolButton>
                        <PoolButton onClick={() => formikProps.submitForm()}>
                            <FormattedMessage id="app.common.save" />
                        </PoolButton>
                    </PoolModalActions>
                </PoolModal>
            )}
        </Formik>
    );
};
